import React, { useState } from 'react';
import InfoDialog from '../common/InfoDialog';
import { useAuth } from '../../contexts/AuthContext';
import { useFeed } from '../../contexts/FeedContext';
import { useProject } from '../../contexts/ProjectContext';
import { 
  Box, 
  Avatar, 
  TextField, 
  Button, 
  Paper,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import styled from '@emotion/styled';
import { 
  AssignmentTurnedIn,
  Article,
  Slideshow,
  FactCheck
} from '@mui/icons-material';
import api from '../../services/api';
import DemoComposer from './DemoComposer';
import StandupComposer from './StandupComposer';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: '16px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '24px',
    padding: '8px 16px',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
      borderWidth: 1,
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '1.1rem',
    padding: '8px 0',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #e0e0e0',
  },
});

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: '8px',
  '&:hover': {
    backgroundColor: 'rgba(94, 53, 177, 0.08)',
  },
}));

const PostButton = styled(Button)({
  borderRadius: '24px',
  fontWeight: 'bold',
  padding: '8px 20px',
  '&:disabled': {
    opacity: 0.5,
  },
});

const CreatePost = () => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [infoDialog, setInfoDialog] = useState({ open: false, title: '', message: '', severity: 'info' });
  const [mode, setMode] = useState('status'); // 'status' | 'task' | 'demo' | 'standup'
  const [taskForm, setTaskForm] = useState({
    project_id: '',
    title: '',
    description: '',
    priority: 'normal',
    due_date: '',
    points: ''
  });
  const [demoForm, setDemoForm] = useState({ video: '', screenshots: '', info: '' });
  const [standupForm, setStandupForm] = useState({ yesterday: '', today: '', blockers: '', stretch: '' });
  const { user } = useAuth();
  const { refresh } = useFeed();
  const { projects } = useProject();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (mode === 'task') {
      // Validate task fields
      if (!taskForm.project_id || !taskForm.title.trim()) return;
    } else if (mode === 'status') {
      if (!content.trim()) return;
    } else if (mode === 'demo' || mode === 'standup') {
      // Not implemented yet
      setInfoDialog({
        open: true,
        title: 'Feature Not Available',
        message: 'This post type is not implemented yet.',
        severity: 'warning'
      });
      return;
    }
  
    console.log('Submitting', mode, 'by user:', user?.id);
    
    if (!user) {
      console.error('No user found. User must be logged in to post.');
      return;
    }
  
    setIsSubmitting(true);
    try {
      if (mode === 'task') {
        // Create task via backend then assign to current user
        const payload = {
          project_id: taskForm.project_id,
          title: taskForm.title.trim(),
          description: taskForm.description.trim() || undefined,
          priority: taskForm.priority || undefined,
          due_date: taskForm.due_date || undefined,
          points: taskForm.points === '' ? undefined : Number(taskForm.points),
        };
        const res = await api.post('/tasks', payload);
        const created = res.data?.task || res.data;
        if (created?.id) {
          try { await api.post(`/tasks/${created.id}/accept`); } catch {}
        }
        // reset
        setTaskForm({ project_id: '', title: '', description: '', priority: 'normal', due_date: '', points: '' });
        setMode('status');
        await refresh();
      } else if (mode === 'status') {
        // First, ensure the user has a profile in Supabase then create a normal post
                // This logic should be handled by the backend or a user context.
        // For now, we assume the user profile exists if they are logged in.
        const profile = { id: user.id }; // Mock profile check

        if (!profile) {
          // In a real app, you might want to handle this case, but for now we assume profile exists.
          console.error('User profile not found.');
          return;
        }

        const response = await api.post('/posts', { content: content.trim() });
        const { error } = { data: response.data.post, error: null };

        if (error) throw error;
        setContent('');
        await refresh();
      }
    } catch (error) {
      console.error('Error creating post:', error);
      setInfoDialog({
        open: true,
        title: 'Post Creation Failed',
        message: `Failed to create post: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <>
      <StyledPaper elevation={0}>
      <form onSubmit={handleSubmit}>
        <Box display="flex" p={1}>
          <Avatar 
            src={user?.user_metadata?.avatar_url} 
            alt={user?.user_metadata?.full_name || 'User'}
            sx={{ width: 48, height: 48, mr: 2 }}
          />
          <Box flex={1}>
            {mode === 'task' ? (
              <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                  <Select
                    size="small"
                    value={taskForm.project_id}
                    onChange={(e) => setTaskForm((f) => ({ ...f, project_id: e.target.value }))}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>Select Project</MenuItem>
                    {projects.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                  <TextField
                    size="small"
                    label="Task title"
                    fullWidth
                    value={taskForm.title}
                    onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))}
                  />
                  <Select
                    size="small"
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm((f) => ({ ...f, priority: e.target.value }))}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    label="Due date"
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm((f) => ({ ...f, due_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    size="small"
                    label="Points"
                    type="number"
                    sx={{ width: 120 }}
                    value={taskForm.points}
                    onChange={(e) => setTaskForm((f) => ({ ...f, points: e.target.value }))}
                  />
                </Stack>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Task description (optional)"
                  variant="outlined"
                  value={taskForm.description}
                  onChange={(e) => setTaskForm((f) => ({ ...f, description: e.target.value }))}
                  disabled={isSubmitting}
                />
              </Box>
            ) : mode === 'demo' ? (
              <DemoComposer form={demoForm} onChange={setDemoForm} disabled={isSubmitting} />
            ) : mode === 'standup' ? (
              <StandupComposer form={standupForm} onChange={setStandupForm} disabled={isSubmitting} />
            ) : (
              <StyledTextField
                fullWidth
                multiline
                maxRows={6}
                placeholder="What's happening?"
                variant="outlined"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSubmitting}
                inputProps={{
                  'data-testid': 'post-input'
                }}
              />
            )}
            
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <Box>
                {/* Post Type Switcher */}
                <Tooltip title="Status Update">
                  <ActionButton onClick={() => setMode('status')} aria-label="mode-status" sx={{ color: mode === 'status' ? 'primary.main' : undefined }}>
                    <Article />
                  </ActionButton>
                </Tooltip>
                <Tooltip title="Create Task">
                  <ActionButton onClick={() => setMode('task')} aria-label="mode-task" sx={{ color: mode === 'task' ? 'primary.main' : undefined }}>
                    <AssignmentTurnedIn />
                  </ActionButton>
                </Tooltip>
                <Tooltip title="Demo">
                  <ActionButton onClick={() => setMode('demo')} aria-label="mode-demo" sx={{ color: mode === 'demo' ? 'primary.main' : undefined }}>
                    <Slideshow />
                  </ActionButton>
                </Tooltip>
                <Tooltip title="Stand-up">
                  <ActionButton onClick={() => setMode('standup')} aria-label="mode-standup" sx={{ color: mode === 'standup' ? 'primary.main' : undefined }}>
                    <FactCheck />
                  </ActionButton>
                </Tooltip>
                
              </Box>
              
              <PostButton
                variant="contained"
                color="primary"
                type="submit"
                disabled={
                  isSubmitting ||
                  (mode === 'task' ? (!taskForm.project_id || !taskForm.title.trim()) : false) ||
                  (mode === 'status' ? !content.trim() : false) ||
                  (mode === 'demo' || mode === 'standup')
                }
              >
                {isSubmitting
                  ? (mode === 'task' ? 'Creating...' : 'Posting...')
                  : (mode === 'task' ? 'Create Task' : mode === 'status' ? 'Post' : 'Coming Soon')}
              </PostButton>
            </Box>
          </Box>
        </Box>
      </form>
    </StyledPaper>

    <InfoDialog
      open={infoDialog.open}
      onClose={() => setInfoDialog({ open: false, title: '', message: '', severity: 'info' })}
      title={infoDialog.title}
      message={infoDialog.message}
      severity={infoDialog.severity}
    />
    </>
  );
};

export default CreatePost;
